import { createSelector } from 'reselect';
import { groupBy, reject, sortBy, size, values, getOr, keyBy } from 'lodash/fp';
import type { RootState } from 'app/store/reducers';
import { FetchStatus } from 'app/lib/constants';
import {
    type JobRunTimelineEntry,
    RUN_FULL_MATERIALIZATION,
    RUN_INCREMENTAL_MATERIALIZATION,
    type SnowflakeStageFqn
} from 'app/lib/types/server';
import { selectIsJobManagementEnabled } from 'app/store/applicationConfig/selectors.js';
import { selectUserCanManageJobs } from 'app/store/users/selectors.js';
import { compare, fqnEquals, toStringFqn, splitFqn } from 'app/lib/utils';
import { getFilterOptions, filterValues } from './utils.js';

const selectSlice = (state: RootState) => state.jobs;

const selectAuditDataInformation = createSelector([selectSlice], ({ auditData }) => auditData);

export const selectAuditData = createSelector(
    [selectAuditDataInformation],
    (auditData) => auditData?.data
);

export const selectIsLoadingAuditData = createSelector(
    [selectAuditDataInformation],
    (auditData) => auditData?.isLoading
);

const selectActivityMetrics = createSelector(
    [selectSlice],
    ({ activityMetrics }) => activityMetrics
);

export const selectMetrics = createSelector(
    [selectActivityMetrics],
    (activityMetrics) => activityMetrics?.metrics
);

export const selectIsLoadingMetrics = createSelector(
    [selectActivityMetrics],
    (activityMetrics) => activityMetrics?.isLoading
);

const selectFilters = createSelector([selectSlice], ({ filters }) => filters);

export const selectFiltersOptions = createSelector([selectFilters], ({ options }) => options);

const selectServiceAccounts = createSelector(
    [selectSlice],
    ({ serviceAccounts }) => serviceAccounts
);

export const selectServiceAccountsData = createSelector([selectServiceAccounts], ({ data }) =>
    Object.values(data ?? {})
);

export const selectProjectOptions = createSelector([selectSlice], ({ projects }) => projects);

const selectEnvironmentsById = createSelector(
    [selectSlice],
    ({ environmentsById }) => environmentsById
);

export const selectEnvironments = createSelector([selectEnvironmentsById], (environmentsById) =>
    Object.values(environmentsById)
);

const selectPropEnvUid = (_: RootState, props: { envUid: string }) => props.envUid;

export const selectEnvironmentForPropEnvUid = createSelector(
    selectEnvironmentsById,
    selectPropEnvUid,
    (environments, uid) => environments[uid]
);

const selectPropUid = (_: RootState, props: { uid: string }) => props.uid;
const selectPropStageFqn = (_: RootState, props: { fqn: SnowflakeStageFqn }) => props.fqn;

export const selectJobEnvironmentRoleForId = createSelector(
    [selectPropUid, selectEnvironmentsById],
    (environmentUid, environmentsById) => environmentsById[environmentUid]?.role
);

export const selectDefaultValuesForEnvId = createSelector(
    [selectPropUid, selectEnvironmentsById],
    (environmentUid, environmentsById) => ({
        database: environmentsById[environmentUid]?.database,
        schema: environmentsById[environmentUid]?.schema
    })
);

export const selectIsLoadingJobDefinitions = createSelector(
    [selectSlice],
    ({ isLoadingJobDefinitions }) => isLoadingJobDefinitions
);

export const selectHasJobDefinitions = createSelector(
    [selectSlice],
    (slice) => Object.keys(slice.jobDefinitionsById ?? {}).length > 0
);

const selectJobDefinitionsById = createSelector(selectSlice, getOr({}, 'jobDefinitionsById'));

export const selectJobDefinitions = createSelector(selectJobDefinitionsById, values);

export const selectJobDefinitionForId = createSelector(
    [selectSlice, selectPropUid],
    (slice, definitionUid) => slice.jobDefinitionDetails.byId[definitionUid]
);

export const selectIsLoadingJobDefinitionForId = createSelector(
    [selectSlice, selectPropUid],
    (slice, definitionUid) => slice.jobDefinitionDetails.isLoadingById[definitionUid]
);

export const selectJobRun = createSelector([selectSlice], ({ jobRun }) => jobRun?.data);

export const selectSelectedJobDefinition = createSelector(
    [selectSlice],
    ({ selectedJobDefinitionUid }) => selectedJobDefinitionUid
);

export const selectSelectedEnvironment = createSelector(
    [selectSlice],
    ({ selectedEnvironmentUid }) => selectedEnvironmentUid
);

export const selectSelectedServiceAccount = createSelector(
    [selectSlice],
    ({ selectedServiceAccountUid }) => selectedServiceAccountUid
);

export const selectJobRuns = createSelector(
    [selectSlice],
    ({ jobRunHistory }) => jobRunHistory?.data
);

export const selectLastJobRun = createSelector([selectSlice], ({ lastJobRun }) => lastJobRun?.data);
export const selectIsLoadingLastJobRun = createSelector(
    [selectSlice],
    ({ lastJobRun }) => lastJobRun?.isLoading
);

export const selectTotalJobRunsCount = createSelector(
    [selectSlice],
    ({ jobRunHistory }) => jobRunHistory?.totalCount || 0
);

export const selectIsLoadingJobRuns = createSelector(
    [selectSlice],
    ({ jobRunHistory }) => jobRunHistory?.isLoading
);

function pushToKeyInCollection<T>(collection: { [key: string]: T[] }, key: string, value: T) {
    const entry = collection[key] ?? (collection[key] = []);
    entry.push(value);
}

const isChildRun = (run: { path: string[] }) => run.path.length >= 2;

interface ExpandedJobRunTimelineEntry extends JobRunTimelineEntry {
    parentDefinitionUid?: string;
    hasChildren?: boolean;
    hasSibling?: boolean;
}

export const selectJobRunTimeline = createSelector([selectSlice], ({ jobRunTimeline }) => {
    const { now, entries } = jobRunTimeline;

    const runsByEntryId: { [key: string]: ExpandedJobRunTimelineEntry } = {};
    const childrenByEntryId: { [key: string]: string[] } = {};

    for (const run of entries) {
        runsByEntryId[run.entryId] = { ...run };
    }

    const runs = Object.values(runsByEntryId);
    const rootRuns = reject(isChildRun, runs);
    const groupsById = groupBy('definitionUid', rootRuns);

    for (const [entryId, run] of Object.entries(runsByEntryId)) {
        const parentId = run.path.at(-2);
        if (parentId) {
            pushToKeyInCollection(childrenByEntryId, parentId, entryId);

            run.parentDefinitionUid = runsByEntryId[parentId]!.definitionUid;

            const rootId = run.path.at(0);

            if (rootId) {
                const rootDefinitionUid = runsByEntryId[rootId]!.definitionUid;
                pushToKeyInCollection(groupsById, rootDefinitionUid, run);
            }
        }
    }

    // Set child and sibling flags for determining corner radii
    for (const [entryId, childIds] of Object.entries(childrenByEntryId)) {
        runsByEntryId[entryId]!.hasChildren = childIds.length > 0;
        for (let i = childIds.length - 2; i >= 0; i--) {
            runsByEntryId[childIds[i]!]!.hasSibling = true;
        }
    }

    // Sort the runs within each group based on their path so we
    // display the correct hierarchy
    for (const definitionUid in groupsById) {
        groupsById[definitionUid] = sortBy(({ path }) => path.join('.'), groupsById[definitionUid]);
    }

    const sortedByDefinitionUid: { [key: string]: ExpandedJobRunTimelineEntry[] } = {};
    const keys = Object.keys(groupsById).sort();
    for (const key of keys) {
        sortedByDefinitionUid[key] = groupsById[key]!;
    }

    return {
        now,
        runsByDefinition: sortedByDefinitionUid
    };
});

export const selectIsLoadingJobRunTimeline = createSelector(
    [selectSlice],
    ({ jobRunTimeline }) => jobRunTimeline.status === FetchStatus.FETCHING
);

export const selectIsDeletingJobDefinition = createSelector(
    [selectSlice],
    ({ isDeletingJobDefinition }) => isDeletingJobDefinition
);

export const selectIsDeletingJobEnvironment = createSelector(
    [selectSlice],
    ({ isDeletingJobEnvironment }) => isDeletingJobEnvironment
);

export const selectIsDeletingServiceAccount = createSelector(
    [selectSlice],
    ({ isDeletingServiceAccount }) => isDeletingServiceAccount
);

export const selectIsCancelingJobRun = createSelector(
    [selectSlice],
    ({ isCancelingJobRun }) => isCancelingJobRun
);

const selectSetEnvironment = createSelector([selectSlice], ({ setEnvironment }) => setEnvironment);

const selectSetServiceAccount = createSelector(
    [selectSlice],
    ({ setServiceAccount }) => setServiceAccount
);
export const selectSetEnvironmentIsProcessing = createSelector(
    [selectSetEnvironment],
    ({ status }) => status === FetchStatus.FETCHING
);

export const selectSetEnvironmentError = createSelector(
    [selectSetEnvironment],
    ({ error }) => error
);

const selectSetJobDefinition = createSelector(
    [selectSlice],
    ({ setJobDefinition }) => setJobDefinition
);

export const selectSetJobDefinitionIsProcessing = createSelector(
    [selectSetJobDefinition],
    ({ status }) => status === FetchStatus.FETCHING
);

export const selectSetJobDefinitionError = createSelector(
    [selectSetJobDefinition],
    ({ error }) => error
);

export const selectSetServiceAccountIsProcessing = createSelector(
    [selectSetServiceAccount],
    ({ status }) => status === FetchStatus.FETCHING
);

export const selectSetServiceAccountError = createSelector(
    [selectSetServiceAccount],
    ({ error }) => error
);

export const selectStoredProcedureCandidates = createSelector(
    [selectSlice],
    (slice) => slice.storedProcedureCandidatesList.options ?? null
);

export const selectStoredProcedureCandidatesError = createSelector(
    [selectSlice],
    (slice) => slice.storedProcedureCandidatesList.error
);

export const selectIsLoadingStoredProcedureCandidates = createSelector(
    [selectSlice],
    (slice) => slice.storedProcedureCandidatesList.isLoading
);

export const selectTriggerCandidatesByRole = createSelector([selectSlice], (slice) => {
    const candidates = slice.candidatesByRole.options?.entries;
    return candidates ? [...candidates].sort(compare('definitionRef.name')) : [];
});

export const selectIsLoadingTriggerCandidatesByRole = createSelector(
    [selectSlice],
    (slice) => slice.candidatesByRole.isLoading
);

export const selectTriggerCandidatesByRoleError = createSelector(
    [selectSlice],
    (slice) => slice.candidatesByRole.error
);

export const selectTriggerCandidatesFilterOptions = createSelector(
    selectTriggerCandidatesByRole,
    selectPropUid,
    getFilterOptions
);

export const selectTriggerCandidatesFilter = createSelector(
    [selectSlice],
    ({ candidatesByRole }) => candidatesByRole.filter
);

export const selectFilteredEventDrivenTriggerCandidates = createSelector(
    selectTriggerCandidatesByRole,
    selectTriggerCandidatesFilter,
    selectPropUid,
    filterValues
);

export const selectJobDefinitionsFilterOptions = createSelector(
    selectJobDefinitions,
    getFilterOptions
);

export const selectJobDefFilter = createSelector([selectSlice], (slice) => slice.jobDefFilters);

export const selectFilteredJobDefinition = createSelector(
    selectJobDefinitions,
    selectJobDefFilter,
    filterValues
);

export const selectTriggerCandidatesCounts = createSelector(
    selectTriggerCandidatesByRole,
    selectFilteredEventDrivenTriggerCandidates,
    selectPropUid,
    (candidates, filteredCandidates, currentJobUid) => ({
        all: size(candidates.filter((option) => option.definitionRef.uid !== currentJobUid)),
        filtered: size(filteredCandidates)
    })
);

export const selectJobNames = createSelector([selectJobDefinitions], (jobDefinitions) =>
    jobDefinitions.map(({ name }) => name)
);

const selectCreateJobDefinitionDialog = createSelector(
    [selectSlice],
    ({ createJobDefinitionDialog }) => createJobDefinitionDialog
);

const selectStagesListSlice = createSelector(
    [selectCreateJobDefinitionDialog],
    ({ stagesList }) => stagesList
);
export const selectSnowflakeStagesList = createSelector(
    [selectStagesListSlice],
    (stagesList) => stagesList.list
);
export const selectSnowflakeStageUrl = createSelector(
    [selectSnowflakeStagesList, selectPropStageFqn],
    (stagesList, fqn) => stagesList?.find((stage) => fqnEquals(stage.stage, fqn))?.url
);
export const selectSnowflakeStagesListIsLoading = createSelector(
    [selectStagesListSlice],
    (stagesList) => stagesList.isLoading
);
export const selectSnowflakeStagesListError = createSelector(
    [selectStagesListSlice],
    (stagesList) => stagesList.error
);

const selectStagesDetailsSlice = createSelector(
    [selectCreateJobDefinitionDialog],
    ({ stagesDetails }) => stagesDetails
);
export const selectSnowflakeStageDetailsForStage = createSelector(
    [selectStagesDetailsSlice, selectPropStageFqn],
    (stagesDetailsSlice, fqn) => stagesDetailsSlice.byStageFqn[toStringFqn(fqn)]
);

export const selectSnowflakeStageDetailsIsLoading = createSelector(
    [selectStagesDetailsSlice],
    ({ isLoading }) => isLoading
);
export const selectSnowflakeStageDetailsError = createSelector(
    [selectStagesDetailsSlice],
    ({ error }) => error
);

const selectFileFormatsSlice = createSelector(
    [selectCreateJobDefinitionDialog],
    ({ fileFormats }) => fileFormats
);
export const selectFileFormats = createSelector([selectFileFormatsSlice], ({ list }) => list);
export const selectFileFormatsIsLoading = createSelector(
    [selectFileFormatsSlice],
    ({ isLoading }) => isLoading
);
export const selectFileFormatsError = createSelector(
    [selectFileFormatsSlice],
    ({ error }) => error
);

const selectStageFilesSlice = createSelector(
    [selectCreateJobDefinitionDialog],
    ({ stageFiles }) => stageFiles
);
export const selectSnowflakeStageFilesIsLoading = createSelector(
    [selectStageFilesSlice],
    ({ isLoading }) => isLoading
);
export const selectSnowflakeStageFiles = createSelector(
    [selectStageFilesSlice],
    ({ list }) => list
);
export const selectSnowflakeStageFilesError = createSelector(
    [selectStageFilesSlice],
    ({ error }) => error
);

const selectStageFileInfoSlice = createSelector(
    [selectCreateJobDefinitionDialog],
    ({ stageFileInfo }) => stageFileInfo
);
export const selectSnowflakeStageFileInfoIsLoading = createSelector(
    [selectStageFileInfoSlice],
    ({ isLoading }) => isLoading
);
export const selectSnowflakeStageFileInfoRawData = createSelector(
    [selectStageFileInfoSlice],
    ({ info }) => info?.rawData
);
export const selectSnowflakeStageFileInfoColumns = createSelector(
    [selectStageFileInfoSlice],
    ({ info }) => info?.columns
);
export const selectSnowflakeStageFileInfoFormatProperties = createSelector(
    [selectStageFileInfoSlice],
    ({ info }) => info?.fileFormatProperties
);
export const selectSnowflakeStageFileInfoError = createSelector(
    [selectStageFileInfoSlice],
    ({ error }) => error
);

export const selectIsSubmittingJobRun = createSelector(
    [selectSlice],
    (slice) => slice.isSubmittingJobRun
);

export const selectRunningJobs = createSelector(
    [selectSlice],
    ({ runningJobsByRunId }) => runningJobsByRunId
);
export const selectIsJobRunningForUid = createSelector(
    [selectPropUid, selectRunningJobs],
    (jobUid, runningJobsByRunId) => Object.values(runningJobsByRunId).includes(jobUid)
);

const selectDatabasesByEnvironment = createSelector(
    [selectSlice],
    (slice) => slice.databasesByEnvironment
);

export const selectIsLoadingDatabasesForEnvironment = createSelector(
    [selectDatabasesByEnvironment],
    ({ status }) => status === FetchStatus.FETCHING
);

export const selectDatabasesForPropEnvironmentUid = createSelector(
    [selectDatabasesByEnvironment, selectPropUid],
    ({ environmentUid, entries }, uid) => (environmentUid === uid ? entries : [])
);

export const selectIsLoadingModelTagCheckCandidates = createSelector(
    [selectSlice],
    (slice) => slice.modelTagCheckCandidates.isLoading
);

const MODEL_OP_TYPES = [RUN_FULL_MATERIALIZATION, RUN_INCREMENTAL_MATERIALIZATION];

export const selectModelTagCheckCandidates = createSelector([selectSlice], (slice) => {
    const triggerCandidates = slice.candidatesByRole.options?.entries;
    const triggerCandidatesByFqn = keyBy('managedTableFqn', triggerCandidates);

    return slice.modelTagCheckCandidates.options?.map((candidate) => {
        const operationType = triggerCandidatesByFqn[candidate.inputTable]?.operationType;

        return {
            ...candidate,
            inputTable: splitFqn(candidate.inputTable),
            isModel: MODEL_OP_TYPES.includes(operationType ?? '')
        };
    });
});

export const selectIsLoadingModelTagSourceTables = createSelector(
    [selectSlice],
    (slice) => slice.modelTagSourceTables.isLoading
);

export const selectModelTagSourceTables = createSelector(
    [selectSlice],
    (slice) => slice.modelTagSourceTables.options
);

export const selectIsJobsEnabled = createSelector(
    [selectIsJobManagementEnabled, selectUserCanManageJobs],
    (isJobManagementEnabled, canManageJobs) => isJobManagementEnabled || canManageJobs
);
